<template>
  <div class="add-webpage flex-grow-1 d-flex flex-column overflow-y-hidden">
    <a-form id="add-webpage-form" v-scrollToInvalidField class="flex-grow-1 d-flex flex-column overflow-y-hidden" :form="addWebpageForm" @submit="onSubmit">
      <div class="a-header">
        <span class="a-header--title slide-down-enter-active">Add Webpage</span>
      </div>
      <div class="a-body p-20-px slide-up-enter-active flex-grow-1">
        <a-row :gutter="24">
          <a-col :span="24" v-if="isLimitUrl">
            <a-alert type="error" message="URL Limit has reached. Please upgrade or contact us." banner />
          </a-col>
          <a-col :span="24">
            <a-form-item>
              <a-radio-group v-model="mode" :disabled="isLimitUrl">
                <a-radio :value="1">Single</a-radio>
                <a-radio :value="2">Multiples</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="24">
            <div v-if="isSuccessMultipleBaseline">
              <div class="success-rebaseline-container">
                <div>
                  <a-icon type="check-circle" theme="filled" class="check-success" />
                </div>
                <div class="ml-3">
                  <p class="mb-1 notice-text">{{ multipleReBaselineData.totalUrls }} webpages has been queued for rebaseline.</p>
                  <p v-if="multipleReBaselineData.batches.length > 1" class="mb-1 notice-text">
                    <!-- Each API call can only handle 200 URLs at a time -->
                    <!-- A possible reason why we are limiting the number of URLs per batch is to avoid the API call payload size being too large, which could cause the API call to fail -->
                    <!-- We do not actually care if they send like 1000 URLs for baselining at a time, as long as the total number of URLs is within the quota because all baselining is queued -->
                    <strong>Split into {{ multipleReBaselineData.batches.length }} batch jobs.</strong>
                  </p>
                  <p class="mb-1 notice-text">
                    This may take a few minutes to a few hours depending on the number of pages.
                  </p>
                  <p class="mb-1 notice-text">
                    You may check the status of {{ multipleReBaselineData.batches.length > 1 ? 'these batch jobs' : 'this batch job' }}:
                  </p>
                  <ul class="batch-list mb-1">
                    <li v-for="batch in multipleReBaselineData.batches" :key="batch.batchId" class="notice-text">
                      <router-link
                        class="nav-link-detail"
                        :to="{ path: '/batch-jobs/detail/BL' + batch.batchId }"
                      >
                        Batch ID BL{{ batch.batchId }}
                      </router-link>
                      <!-- batch.total is the total number of URLs in the batch -->
                      <!-- batch.processed is the number of URLs that have been processed, so why this is different -->
                      <!-- is because some URL might have failed to be processed due to duplicates or invalid URLs etc -->
                      ({{ batch.total }} URLs<span v-if="batch.processed !== batch.total">, {{ batch.processed }} processed (Some URLs might have failed to be processed due to duplicates or invalid URLs)</span>)
                    </li>
                  </ul>
                  <p class="mb-0 notice-text">An email will be sent to {{ parentUserName }} when each job is finished.</p>
                </div>
              </div>
              <div v-if="multipleReBaselineData.quotaExhausted" class="warning-container mt-2">
                <div>
                  <a-icon type="warning" theme="filled" class="warning-icon" />
                </div>
                <div class="ml-3">
                  <p class="mb-0 notice-text">
                    <strong>Quota limit reached.</strong> {{ multipleReBaselineData.remainingUrlsCount }} URLs could not be processed.
                  </p>
                </div>
              </div>
              <div v-if="multipleReBaselineData.failedBatch" class="error-container mt-2">
                <div>
                  <a-icon type="close-circle" theme="filled" class="error-icon" />
                </div>
                <div class="ml-3">
                  <p class="mb-1 notice-text">
                    <strong>Batch {{ multipleReBaselineData.failedBatch.chunkIndex }} failed</strong> ({{ multipleReBaselineData.failedBatch.urlCount }} URLs)
                  </p>
                  <p class="mb-1 notice-text">
                    {{ multipleReBaselineData.failedBatch.error.join(', ') }}
                  </p>
                  <p v-if="multipleReBaselineData.failedBatch.remainingCount > multipleReBaselineData.failedBatch.urlCount" class="mb-0 notice-text">
                    {{ multipleReBaselineData.failedBatch.remainingCount - multipleReBaselineData.failedBatch.urlCount }} additional URLs were not processed.
                  </p>
                </div>
              </div>
            </div>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="24">
            <a-form-item :extra="mode === 2 ? 'Enter (or paste) your list of webpages below (URLs will be processed in batches based on your plan quota)' : ''">
              <template slot="label">
                Webpage URL
                <a-tooltip placement="top">
                  <template slot="title">
                    <span class="fs-10">
                      The URL of the webpage to be monitored.
                    </span>
                  </template>
                  <a-icon type="info-circle" theme="filled" />
                </a-tooltip>
              </template>
              <a-input
                v-decorator="[
                  'webpageUrl',
                  {
                    rules: [
                      {
                        required: true,
                        message: 'Please input URL!'
                      },
                      {
                        pattern: REGEX_URL_WITH_PROTOCOL,
                        message: 'Please input valid URL! (eg. https://www.example.com)'
                      }
                    ]
                  }
                ]"
                placeholder="URL"
                type="text"
                v-if="mode === 1"
                :disabled="isLimitUrl"
              />
              <a-textarea
                v-decorator="[
                  'webpageUrls',
                  {
                    rules: [
                      {
                        required: true,
                        message: 'Please input URLs!'
                      }
                    ]
                  }
                ]"
                :autosize="{ minRows: 5, maxRows: 7 }"
                placeholder="eg. http://www.banffcyber.com/index.html (for regular HTTP)
https://www.banffcyber.com/index.html (for secure HTTP) 
http://www.banffcyber.com:8080/demo/demo.html (for pages running on a non-default port, e.g. 8080)"
                v-if="mode === 2"
                :disabled="isLimitUrl"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="12">
            <a-form-item label="Plan type">
              <a-select
                v-decorator="[
                  'planType',
                  {
                    rules: [{ required: true, message: 'Please select the plan type' }]
                  }
                ]"
                size="large"
                :disabled="isLimitUrl"
              >
                <template v-for="(valuePlanType, keyPlanType) in planTypes">
                  <a-select-option :key="keyPlanType" :value="keyPlanType" v-if="valuePlanType.limit - valuePlanType.count">
                    {{ keyPlanType | lowercase | capitalize }} - {{ valuePlanType.limit - valuePlanType.count }}
                    URLs left
                  </a-select-option>
                </template>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item>
              <template slot="label">
                User Agent
                <a-tooltip placement="top">
                  <template slot="title">
                    <span class="fs-10"> Selects the user agent to be used.<br />We provide both desktop and mobile user agents </span>
                  </template>
                  <a-icon type="info-circle" theme="filled" />
                </a-tooltip>
              </template>
              <a-select
                v-decorator="[
                  'userAgentId',
                  {
                    rules: [
                      {
                        required: true,
                        message: 'Please select the user agent id'
                      }
                    ]
                  }
                ]"
                size="large"
                :disabled="isLimitUrl"
              >
                <a-select-option v-for="userAgent in userAgents" :key="userAgent.id" :value="userAgent.id">
                  {{ userAgent.alias }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="24">
            <a-form-item>
              <a-checkbox :disabled="isLimitUrl" :checked="isOpenAdvOptions" @change="isOpenAdvOptions = !isOpenAdvOptions">
                Advanced Options
              </a-checkbox>
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="20" v-show="isOpenAdvOptions">
          <a-row :gutter="24">
            <a-col v-if="featureAccess && featureAccess['tags']" :span="12">
              <a-form-item>
                <template slot="label">
                  Tags
                  <a-tooltip placement="top">
                    <template slot="title">
                      <span class="fs-10">
                        Specify the tags that should be applied to the webpage.
                      </span>
                    </template>
                    <a-icon type="info-circle" theme="filled" />
                  </a-tooltip>
                </template>
                <div class="d-flex">
                  <a-select
                    class="flex-grow-1"
                    mode="multiple"
                    v-decorator="[
                      'tagIds',
                      {
                        rules: []
                      }
                    ]"
                    size="large"
                    placeholder="Select tags"
                    show-search
                    option-filter-prop="children"
                    :disabled="isLimitUrl"
                  >
                    <a-select-option v-for="tag in availableTags" :key="tag.id" :value="tag.id">
                      {{ tag.name }}
                    </a-select-option>
                  </a-select>
                  <a-button class="ml-2" type="primary" icon="plus" @click="openAddTagModal" :disabled="isLimitUrl">
                    Add
                  </a-button>
                </div>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item extra="separate different keywords by a comma (,)">
                <template slot="label">
                  Keyword(s) to include
                  <a-tooltip placement="top" :overlayStyle="{ 'max-width': 'max-content' }">
                    <template slot="title">
                      <span class="fs-10">
                        Specify the whitelisted keyword(s) that we should monitor on your webpage.<br />
                        An alert will be sent if the whitelisted keywords are missing from the page.<br />
                        Examples: company name, webpage title.
                      </span>
                    </template>
                    <a-icon type="info-circle" theme="filled" />
                  </a-tooltip>
                </template>
                <a-textarea v-decorator="['keywordsInclude']" class="row-6 mb-0" :rows="6" />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item extra="separate different keywords by a comma (,)">
                <template slot="label">
                  Keyword(s) to exclude
                  <a-tooltip placement="top" :overlayStyle="{ 'max-width': 'max-content' }">
                    <template slot="title">
                      <span class="fs-10">
                        Specify what blacklisted keyword(s) that should not appear on your webpage.<br />
                        An alert will be sent if the blacklisted keywords are detected on the page.<br />
                        Examples: mysql/php error.
                      </span>
                    </template>
                    <a-icon type="info-circle" theme="filled" />
                  </a-tooltip>
                </template>
                <a-textarea v-decorator="['keywordsExclude']" class="row-6 mb-0" :rows="6" />
              </a-form-item>
            </a-col>
          </a-row>
        </a-row>
      </div>
      <div class="a-footer">
        <a-button key="back" size="large" class="mr-3 slide-down-enter-active" @click="onCancel()">
          Cancel
        </a-button>
        <a-button :disabled="isLimitUrl" key="submit" size="large" type="primary" html-type="submit" class="slide-down-enter-active">
          Save
        </a-button>
      </div>
    </a-form>
    <a-modal v-model="isShowProgress" class="progress-modal" :footer="null" title="" :closable="false" :maskClosable="false">
      <div>
        <a-progress strokeLinecap="square" :percent="percent" status="active" />
        <p class="mb-1 text-center progress-notice"><a-icon type="sync" spin /> {{ progressNotice }}</p>
        <a-alert
          message="We are doing an intelligent baseline of your page now.
          This process will take about 2 - 3 minutes depending on the contents of the webpage."
          type="warning"
          showIcon
        />
      </div>
    </a-modal>
    <a-modal
      v-model="isShowAddTagModal"
      title="Create Tag"
      :confirmLoading="isCreatingTag"
      :maskClosable="false"
      @ok="handleCreateTag"
      @cancel="closeAddTagModal"
    >
      <p class="mb-2">Create a new tag for this user.</p>
      <a-input
        v-model="newTagName"
        placeholder="Enter tag name"
        :disabled="isCreatingTag"
        @pressEnter="handleCreateTag"
      />
    </a-modal>
  </div>
</template>

<script>
  import { REGEX_URL_WITH_PROTOCOL } from '@constants/regex';
  import { flatMapErrMessagesValidateForm, debounceTimeExecute } from '@shared/helpers/utils';
  import WebpageService from '../services/webpage.service';
  import DataService from '@shared/services/data.service';
  import { runBatchBaselineForUrls } from '../helpers/batch-baseline-multiples';
  import { DEFAULT_USER_AGENT_ID } from '../consts/Webpage.consts';
  import AccountService from '../../Account/services/account.service';
  import TagsService from '../../Tags/services/tags.service';

  export default {
    name: 'WebpageAdd',
    data() {
      return {
        REGEX_URL_WITH_PROTOCOL,
        isOpenAdvOptions: false,
        mode: 1,
        userAgents: [],
        isShowProgress: false,
        percent: 0,
        planTypes: {},
        progressNotice: 'Creating first baseline',
        currentUserName: '',
        parentUserName: '',
        isSuccessMultipleBaseline: false,
        multipleReBaselineData: {
          batches: [],
          totalUrls: 0,
          failedBatch: null,
          quotaExhausted: false,
          remainingUrlsCount: 0
        },
        isLimitUrl: false,
        availableTags: [],
        isShowAddTagModal: false,
        newTagName: '',
        isCreatingTag: false
      };
    },
    computed: {
      featureAccess() {
        return this.$store.state.user.data.feature_access || {};
      }
    },
    beforeCreate() {
      this.addWebpageForm = this.$form.createForm(this, {
        name: 'add-webpage-form',
        mapPropsToFields: () => {
          return {
            webpageUrl: this.$form.createFormField({
              value: ''
            }),
            webpageUrls: this.$form.createFormField({
              value: ''
            }),
            planType: this.$form.createFormField({
              value: 'TRIAL'
            }),
            userAgentId: this.$form.createFormField({
              value: DEFAULT_USER_AGENT_ID
            }),
            keywordsInclude: this.$form.createFormField({
              value: ''
            }),
            keywordsExclude: this.$form.createFormField({
              value: ''
            }),
            tagIds: this.$form.createFormField({
              value: []
            })
          };
        }
      });
    },
    mounted() {
      this.getMasterData();
      this.getParentUser();
      this.currentUserName = (this.$store.state && this.$store.state.user && this.$store.state.user.data && this.$store.state.user.data.username) || '';
    },
    methods: {
      onSubmit(e) {
        e.preventDefault();
        this.addWebpageForm.validateFields((err, values) => {
          if (!err) {
            switch (this.mode) {
              case 1: {
                this.addSingleWebpage(values);
                break;
              }
              case 2: {
                this.addMultiplesWebpage(values);
                break;
              }
            }
          }
          if (err) {
            debounceTimeExecute(flatMapErrMessagesValidateForm(err), this.$showErrorMessage);
          }
        });
      },
      async addMultiplesWebpage(formValue) {
        const urls = (formValue.webpageUrls || '').split('\n').filter((url) => url.trim().length > 0);
        const baseBody = {
          plan_type: formValue.planType,
          user_agent_id: formValue.userAgentId,
          text_string_appear: formValue.keywordsInclude || '',
          text_string_not_appear: formValue.keywordsExclude || '',
          tag_ids: formValue.tagIds || [],
        };

        await this.refreshQuotaData();
        const result = await runBatchBaselineForUrls(urls, baseBody, this.planTypes);
        await this.refreshQuotaData();

        if (result.initialQuotaRejected) {
          this.$showErrorMessage('No quota remaining for the selected plan type.');
          return;
        }

        const { successfulBatches, totalUrls, failedBatch: lastError, quotaExhausted, remainingUrls } = result;

        if (successfulBatches.length > 0) {
          const batchCount = successfulBatches.length;

          if (!lastError && !quotaExhausted) {
            this.$showSuccessMessage(
              batchCount > 1
                ? `${batchCount} batch jobs successfully created for ${totalUrls} webpages`
                : 'Batch job successfully created for multiple webpages'
            );
          } else {
            this.$showSuccessMessage(
              `${successfulBatches.length} batch job(s) created for ${totalUrls} webpages`
            );
          }

          this.addWebpageForm.resetFields();
          this.addWebpageForm.setFieldsValue({
            userAgentId: DEFAULT_USER_AGENT_ID,
            planType: 'TRIAL',
          });
          this.isSuccessMultipleBaseline = true;
          this.multipleReBaselineData = {
            batches: successfulBatches,
            totalUrls,
            failedBatch: lastError,
            quotaExhausted,
            remainingUrlsCount: remainingUrls.length,
          };
        } else if (lastError) {
          this.$showMultiErrorMessage(lastError.error);
        }
      },
      async refreshQuotaData() {
        try {
          const userData = await DataService.getUsers();
          this.planTypes = userData.plan_usage || {};
        } catch (error) {
          // Silently fail - will use cached quota data
        }
      },
      async addSingleWebpage(formValue) {
        const body = {
          url: formValue.webpageUrl,
          plan_type: formValue.planType,
          user_agent_id: formValue.userAgentId,
          text_string_appear: formValue.keywordsInclude || '',
          text_string_not_appear: formValue.keywordsExclude || '',
          tag_ids: formValue.tagIds || []
        };
        try {
          const { job_id, url_id } = await WebpageService.create(body);
          this.isShowProgress = true;
          this.fetchStatusofTheJob(job_id, url_id);
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        }
      },
      async fetchStatusofTheJob(jobId, urlId) {
        try {
          const verifyResponse = await DataService.fetchStatusJob(jobId);
          const { status, result } = verifyResponse;
          if (status === 'Completed') {
            this.isShowProgress = false;
          this.$showSuccessMessage('Added Webpages successfull!');
            this.$router.push(`/webpages/baseline-result/${urlId}`);
          } else if (status === 'Error') {
            this.isShowProgress = false;
            this.percent = 0;
            this.progressNotice = 'Creating first baseline';
            try {
              const resultParsed = JSON.parse(result);
              const errMessage = resultParsed && resultParsed.error && resultParsed && resultParsed.error.message ? resultParsed.error.message : 'Failed to baseline web page';
              this.$showErrorMessage(errMessage);
            } catch {
              this.$showErrorMessage('Failed to baseline web page');
            }
          } else {
            this.progressNotice = result ? JSON.parse(result).message : this.progressNotice;
            const percentPerforming = result ? Math.min(JSON.parse(result).start_progress, 100) : 0;
            this.percent = +percentPerforming.toFixed();
            setTimeout(() => {
              this.fetchStatusofTheJob(jobId, urlId);
            }, 3000);
          }
        } catch (error) {
          this.isShowProgress = false;
          this.$showErrorMessage('The baseline of your webpage is error.');
        }
      },
      async getMasterData() {
        const [userAgentsData, userData, tagsData] = await Promise.all([
          DataService.getUserAgents(),
          DataService.getUsers(),
          TagsService.getList((this.$store.state.user.data || {}).id)
        ]);
        this.userAgents = userAgentsData;
        this.planTypes = userData.plan_usage || {};
        this.availableTags = tagsData;
        this.isLimitUrl = Object.keys(this.planTypes).every((key) => this.planTypes[key].count === this.planTypes[key].limit);
        const isLimitTrial = userData.plan_usage && userData.plan_usage['TRIAL'] && userData.plan_usage['TRIAL'].count === userData.plan_usage['TRIAL'].limit;
        const isLimitBronze = userData.plan_usage && userData.plan_usage['BRONZE'] && userData.plan_usage['BRONZE'].count === userData.plan_usage['BRONZE'].limit;
        const isLimitSilver = userData.plan_usage && userData.plan_usage['SILVER'] && userData.plan_usage['SILVER'].count === userData.plan_usage['SILVER'].limit;
        const isLimitGold = userData.plan_usage && userData.plan_usage['GOLD'] && userData.plan_usage['GOLD'].count === userData.plan_usage['GOLD'].limit;
        const defaultPlayType = !isLimitTrial ? 'TRIAL' : !isLimitBronze ? 'BRONZE' : !isLimitSilver ? 'SILVER' : !isLimitGold ? 'GOLD' : 'Limit has reached';
        this.addWebpageForm.setFieldsValue({ planType: defaultPlayType });
      },
      async getParentUser() {
        const data = await AccountService.getUser();
        this.parentUserName = data.name;
        return data;
      },
      openAddTagModal() {
        this.newTagName = '';
        this.isShowAddTagModal = true;
      },
      closeAddTagModal() {
        this.isShowAddTagModal = false;
        this.newTagName = '';
      },
      async handleCreateTag() {
        const tagName = (this.newTagName || '').trim();
        if (!tagName) {
          this.$showErrorMessage('Please enter a tag name.');
          return;
        }
        // This iterates through availableTags array and checks if the tagName already exists
        if (this.availableTags.some(tag => tag.name === tagName)) {
          this.$showErrorMessage('Tag already exists.');
          return;
        }
        this.isCreatingTag = true;
        try {
          await TagsService.create({ tagName });
          const currentTagIds = this.addWebpageForm.getFieldValue('tagIds') || [];
          this.$showSuccessMessage(`Tag '${tagName}' created successfully.`);
          this.isShowAddTagModal = false;
          await this.getMasterData();
          const newTag = this.availableTags.find(t => t.name === tagName);
          if (newTag && !currentTagIds.includes(newTag.id)) {
            this.addWebpageForm.setFieldsValue({ tagIds: [...currentTagIds, newTag.id] });
          }
        } catch (err) {
          this.$showErrorMessage('Failed to create tag.');
        } finally {
          this.isCreatingTag = false;
          this.newTagName = '';
        }
      },
      onCancel() {
        this.$router.push('/webpages/list');
      }
    }
  };
</script>

<style lang="scss" scoped>
  .success-rebaseline-container {
    padding: 8px 10px;
    border: 1px solid #7ecf42;
    border-radius: 4px;
    background-color: #f3ffea;
    display: flex;
    align-items: flex-start;
    .notice-text {
      font-size: 1rem;
    }
    .check-success {
      font-size: 40px;
      color: rgb(126, 207, 66);
    }
    .batch-list {
      list-style: none;
      padding-left: 0;
      margin-bottom: 0.5rem;
      li {
        margin-bottom: 0.25rem;
        &::before {
          content: "•";
          margin-right: 0.5rem;
          color: rgb(126, 207, 66);
        }
      }
    }
  }
  .warning-container {
    padding: 8px 10px;
    border: 1px solid #faad14;
    border-radius: 4px;
    background-color: #fffbe6;
    display: flex;
    align-items: flex-start;
    .notice-text {
      font-size: 1rem;
    }
    .warning-icon {
      font-size: 40px;
      color: #faad14;
    }
  }
  .error-container {
    padding: 8px 10px;
    border: 1px solid #ff4d4f;
    border-radius: 4px;
    background-color: #fff2f0;
    display: flex;
    align-items: flex-start;
    .notice-text {
      font-size: 1rem;
    }
    .error-icon {
      font-size: 40px;
      color: #ff4d4f;
    }
  }
</style>
